"use client";

import React from "react";
import Image from "next/image";
import { toast } from "react-toastify";
import type { ToastOptions } from "react-toastify";

interface CustomToastContentProps {
  type: "success" | "error";
  message: string;
  closeToast?: () => void;
}

const CustomToastContent: React.FC<CustomToastContentProps> = ({ type, message, closeToast }) => {
  const isSuccess = type === "success";
  const iconSrc = isSuccess ? "/images/check.svg" : "/images/invalid.svg";
  const classes = isSuccess 
    ? "bg-background-success border-success-50 text-success-500" 
    : "bg-error-0 border-error-50 text-error-500";

  return (
    <div className={`${classes} border shadow-soft-3 py-1.5 px-3 gap-2.5 rounded-lg flex items-center text-body-medium/md`}>
      <Image src={iconSrc} alt={isSuccess ? "success" : "error"} width={16} height={16} />
      <span>{message}</span>
      {closeToast && (
        <button
          onClick={closeToast}
          className="ml-2 opacity-70 hover:opacity-100 transition-opacity flex items-center justify-center w-4 h-4"
          aria-label="Close"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="stroke-current"
          >
            <path
              d="M9 3L3 9M3 3L9 9"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

// Check if we're in a browser environment
const isBrowser = typeof window !== "undefined";

export const showToast = {
  success: (message: string, options?: ToastOptions) => {
    if (!isBrowser) return;
    toast(
      ({ closeToast }: { closeToast?: () => void }) => (
        <CustomToastContent type="success" message={message} closeToast={closeToast} />
      ),
      {
        ...options,
        type: "default",
      }
    );
  },
  error: (message: string, options?: ToastOptions) => {
    if (!isBrowser) return;
    toast(
      ({ closeToast }: { closeToast?: () => void }) => (
        <CustomToastContent type="error" message={message} closeToast={closeToast} />
      ),
      {
        ...options,
        type: "default",
      }
    );
  },
};

